import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, Typography, CircularProgress, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getApiUrl } from '../utils/apiUrl';

const pageStyles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(120deg, rgb(175, 227, 251), rgb(241, 195, 248), rgb(250, 236, 190), rgb(185, 224, 242))',
    backgroundSize: '240% 240%',
    p: 2,
    position: 'relative',
    overflow: 'hidden',
    '@keyframes gradientShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
    animation: 'gradientShift 12s ease-in-out infinite',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 420,
      height: 420,
      borderRadius: '50%',
      top: -140,
      left: '15%',
      background: 'radial-gradient(circle at 30% 30%, rgba(132, 94, 194, 0.18), rgba(132, 94, 194, 0) 60%)',
      filter: 'blur(2px)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      width: 520,
      height: 520,
      borderRadius: '50%',
      bottom: -200,
      left: '85%',
      transform: 'translateX(-50%)',
      background: 'radial-gradient(circle at 30% 30%, rgba(48, 176, 235, 0.18), rgba(48, 176, 235, 0) 60%)',
      filter: 'blur(2px)',
      pointerEvents: 'none',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  card: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 400,
    width: '100%',
    borderRadius: 3,
    p: 4,
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.8)',
    background: 'rgba(255,255,255,0.96)',
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mb: 2,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: '#0f172a',
    mb: 1,
  },
  message: {
    color: '#64748b',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    mb: 3,
  },
  btn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.5,
    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
  },
};

const CheckRef = () => {
  const [searchParams] = useSearchParams();
  const refToken = searchParams.get('ref_token');
  const navigate = useNavigate();
  const { setUserFromApi } = useApp();
  const [loading, setLoading] = useState(!!refToken);
  const [error, setError] = useState('');

  const doSuperAppLogin = useCallback(async (token) => {
    const url = getApiUrl('/api/user/auth/superapp-login/');
    const { data } = await axios.get(url, {
      params: { ref_token: token },
      withCredentials: true,
      headers: { accept: 'application/json' },
    });
    setUserFromApi(data);
    navigate('/select-app', { replace: true });
  }, [navigate, setUserFromApi]);

  useEffect(() => {
    if (!refToken) {
      setLoading(false);
      return;
    }

    const handleLogin = async () => {
      try {
        await doSuperAppLogin(refToken);
      } catch (err) {
        const msg = err?.response?.data?.detail ?? err?.response?.data?.message ?? err?.message ?? 'เข้าสู่ระบบด้วย ref_token ไม่สำเร็จ';
        setError(typeof msg === 'string' ? msg : 'เกิดข้อผิดพลาด');
      } finally {
        setLoading(false);
      }
    };

    handleLogin();
  }, [refToken, doSuperAppLogin]);

  if (loading) {
    return (
      <Box sx={pageStyles.root}>
        <Paper elevation={0} sx={pageStyles.card}>
          <Box sx={{ ...pageStyles.iconWrap, bgcolor: 'rgba(99, 102, 241, 0.12)' }}>
            <HourglassEmptyRoundedIcon sx={{ fontSize: 40, color: '#6366f1' }} />
          </Box>
          <Typography sx={pageStyles.title}>กำลังเข้าสู่ระบบ</Typography>
          <Typography sx={pageStyles.message}>
            กรุณารอสักครู่ ระบบกำลังตรวจสอบข้อมูล
          </Typography>
          <CircularProgress size={32} sx={{ color: '#6366f1' }} />
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageStyles.root}>
        <Paper elevation={0} sx={pageStyles.card}>
          <Box sx={{ ...pageStyles.iconWrap, bgcolor: 'rgba(239, 68, 68, 0.12)' }}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: '#ef4444' }} />
          </Box>
          <Typography sx={pageStyles.title}>เกิดข้อผิดพลาด</Typography>
          <Typography sx={pageStyles.message}>{error}</Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/login', { replace: true })}
            sx={{ ...pageStyles.btn, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            กลับไปหน้าเข้าสู่ระบบ
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={pageStyles.root}>
      <Paper elevation={0} sx={pageStyles.card}>
        <Box sx={{ ...pageStyles.iconWrap, bgcolor: 'rgba(245, 158, 11, 0.12)' }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: '#f59e0b' }} />
        </Box>
        <Typography sx={pageStyles.title}>เกิดข้อผิดพลาด</Typography>
        <Typography sx={pageStyles.message}>
          กรุณาเข้าระบบใหม่อีกครั้ง
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ ...pageStyles.btn, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          ย้อนกลับ
        </Button>
      </Paper>
    </Box>
  );
};

export default CheckRef;
